import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  ActivityIndicator,
  Dimensions,
} from 'react-native';

import { Header } from '../components/Header';
import { SubHeader } from '../components/SubHeader';
import {
  Search,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Star,
  ArrowUpDown,
} from './../components/icons';
import {
  KeyboardDismissToolbar,
  ACCESSORY_VIEW_ID,
} from '../components/shared/KeyboardDismissToolbar';
import { StageBadge } from '../components/StageBadge';
import { useAuth } from '../contexts/AuthContext';
import {
  useFeedbackPaginatedQuery,
  FeedbackItem,
  FeedbackPaginationParams,
} from '../hooks/queries/useFeedbackQuery';
import { roleService } from '../services/auth/roleService';
import { dateFormatting } from '../utils/dateFormatting';
import { showsInternalStageDetail } from '../utils/requestorBoundary';

const isWeb = Platform.OS === 'web';

/** Written calendar date. Not parsed as UTC midnight. */
const formatWrittenCalendarDate = (value: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(utcNoon);
};

interface FeedbackViewProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

// Rating filter options
const RATING_OPTIONS = [
  { value: '', label: 'All Ratings' },
  { value: '5', label: '5 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '2', label: '2 Stars' },
  { value: '1', label: '1 Star' },
  { value: 'has_rating', label: 'Has Rating' },
  { value: 'no_rating', label: 'No Rating' },
];

// Modified date filter options
const MODIFIED_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '15', label: 'Last 15 days' },
  { value: '30', label: 'Last 30 days' },
];

// Sort options
const SORT_OPTIONS = [
  { value: 'sys_modified_on', label: 'Modified Date' },
  { value: 'fb_rating', label: 'Rating' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: 'request_id', label: 'Request ID' },
];

export const FeedbackView: React.FC<FeedbackViewProps> = ({
  navigate,
  currentPath,
}) => {
  const { user } = useAuth();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortField, setSortField] = useState('sys_modified_on');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [ratingFilter, setRatingFilter] = useState('');
  const [modifiedFilter, setModifiedFilter] = useState('');
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get('window').width,
  );

  // Dropdown visibility states
  const [showRatingDropdown, setShowRatingDropdown] = useState(false);
  const [showModifiedDropdown, setShowModifiedDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Check if user can access feedback view
  const canAccessFeedbackView = roleService.canAccessFeedbackView(user);
  // Internal-only "Configuration" label on the stage badges (PRD FR7). Keyed on the
  // viewer's `External Requestor` role behind a roles-present guard (PRD FR28).
  const showInternalDetail = showsInternalStageDetail(user);

  // Listen for screen size changes
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  // Redirect if not authorized
  useEffect(() => {
    if (user && !canAccessFeedbackView) {
      navigate('/unauthorized');
    }
  }, [user, canAccessFeedbackView, navigate]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Check if any filters/sort are active (not default)
  const hasActiveFilters = useMemo(() => {
    return (
      searchText !== '' ||
      ratingFilter !== '' ||
      modifiedFilter !== '' ||
      sortField !== 'sys_modified_on' ||
      sortDirection !== 'DESC'
    );
  }, [searchText, ratingFilter, modifiedFilter, sortField, sortDirection]);

  // Clear all filters and sort
  const handleClearFilters = () => {
    setSearchText('');
    setRatingFilter('');
    setModifiedFilter('');
    setSortField('sys_modified_on');
    setSortDirection('DESC');
    setCurrentPage(1);
  };

  // Build pagination params
  const paginationParams: FeedbackPaginationParams = useMemo(
    () => ({
      page: currentPage,
      pageSize,
      orderBy: sortField,
      orderDirection: sortDirection,
      searchText: debouncedSearchText || undefined,
      rating: ratingFilter || undefined,
      modifiedOn: modifiedFilter || undefined,
    }),
    [
      currentPage,
      pageSize,
      sortField,
      sortDirection,
      debouncedSearchText,
      ratingFilter,
      modifiedFilter,
    ],
  );

  // Fetch data
  const { data, isLoading, error, refetch, isFetching } =
    useFeedbackPaginatedQuery(paginationParams);

  const feedbackItems = data?.data || [];
  const pagination = data?.pagination;

  // Don't render if not authorized
  if (!canAccessFeedbackView) {
    return null;
  }

  // Responsive layout
  const shouldShowTable = isWeb && screenWidth >= 768;

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const instant = dateFormatting.format(dateString, {
      locale: 'en-US',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (instant) return instant;
    return formatWrittenCalendarDate(dateString) ?? '-';
  };

  // Render star rating
  const renderStars = (rating: number | null) => {
    if (rating === null) {
      return <Text style={styles.noRating}>No rating</Text>;
    }
    return (
      <View style={styles.starsContainer}>
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            size={14}
            color={i <= rating ? '#FFD700' : '#E0E0E0'}
            fill={i <= rating ? '#FFD700' : 'transparent'}
          />
        ))}
      </View>
    );
  };

  // Handle row click to navigate to request detail at the feedback step
  const handleRowClick = (requestId: string) => {
    navigate(`/request-detail?id=${requestId}&step=summary`);
  };

  // Toggle sort direction or change sort field
  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortDirection(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortField(field);
      setSortDirection('DESC');
    }
    setShowSortDropdown(false);
  };

  // Render loading state
  const renderLoadingState = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color="#ff6b35" />
      <Text style={styles.loadingText}>Loading feedback...</Text>
    </View>
  );

  // Render error state
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <AlertCircle size={48} color="#dc3545" />
      <Text style={styles.errorTitle}>Unable to load feedback</Text>
      <Text style={styles.errorMessage}>
        {(error as Error)?.message || 'An error occurred'}
      </Text>
      <TouchableOpacity style={styles.retryButton} onPress={() => refetch()}>
        <RefreshCw size={16} color="#fff" />
        <Text style={styles.retryButtonText}>Try Again</Text>
      </TouchableOpacity>
    </View>
  );

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Star size={48} color="#ccc" />
      <Text style={styles.emptyTitle}>No feedback found</Text>
      <Text style={styles.emptyMessage}>
        {searchText
          ? 'Try adjusting your search criteria.'
          : 'No feedback has been submitted yet.'}
      </Text>
    </View>
  );

  // Render table header
  const renderTableHeader = () => (
    <View style={styles.tableHeader}>
      <Text style={[styles.headerCell, styles.requestIdColumn]}>ID</Text>
      <Text style={[styles.headerCell, styles.nameColumn]}>Customer</Text>
      <Text style={[styles.headerCell, styles.nameColumn]}>Requestor</Text>
      <Text style={[styles.headerCell, styles.regionColumn]}>Region</Text>
      <Text style={[styles.headerCell, styles.stageColumn]}>Stage</Text>
      <Text style={[styles.headerCell, styles.ratingColumn]}>Rating</Text>
      <Text style={[styles.headerCell, styles.commentsColumn]}>Comments</Text>
      <Text style={[styles.headerCell, styles.dateColumn]}>Modified</Text>
    </View>
  );

  // Render table row
  const renderTableRow = (item: FeedbackItem, index: number) => (
    <TouchableOpacity
      key={item.requestId}
      style={[styles.tableRow, index % 2 === 0 && styles.evenRow]}
      onPress={() => handleRowClick(item.requestId)}
    >
      <Text style={[styles.cell, styles.requestIdColumn, styles.linkText]}>
        {item.requestId}
      </Text>
      <Text style={[styles.cell, styles.nameColumn]} numberOfLines={1}>
        {item.customerName || '-'}
      </Text>
      <Text style={[styles.cell, styles.nameColumn]} numberOfLines={1}>
        {item.requestorName || '-'}
      </Text>
      <Text style={[styles.cell, styles.regionColumn]}>
        {item.region?.toUpperCase() || '-'}
      </Text>
      <View style={styles.stageColumn}>
        <View style={styles.stageBadgeWrapper}>
          <StageBadge
            stage={item.stage}
            size="small"
            showInternalDetail={showInternalDetail}
          />
        </View>
      </View>
      <View style={[styles.ratingColumn]}>{renderStars(item.fbRating)}</View>
      <Text style={[styles.cell, styles.commentsColumn]} numberOfLines={2}>
        {item.fbComments || '-'}
      </Text>
      <Text style={[styles.cell, styles.dateColumn]}>
        {formatDate(item.sysModifiedOn)}
      </Text>
    </TouchableOpacity>
  );

  // Render card (mobile view)
  const renderCard = (item: FeedbackItem) => (
    <TouchableOpacity
      key={item.requestId}
      style={styles.card}
      onPress={() => handleRowClick(item.requestId)}
    >
      <View style={styles.cardTopRow}>
        <Text style={styles.cardRequestId}>#{item.requestId}</Text>
        {renderStars(item.fbRating)}
      </View>
      <Text style={styles.cardCustomerName} numberOfLines={1}>
        {item.customerName || 'No customer name'}
      </Text>
      <View style={styles.cardInfoGrid}>
        <View style={styles.cardInfoItem}>
          <Text style={styles.cardInfoLabel}>REQUESTOR</Text>
          <Text style={styles.cardInfoValue} numberOfLines={1}>
            {item.requestorName || '-'}
          </Text>
        </View>
        <View style={styles.cardInfoItem}>
          <Text style={styles.cardInfoLabel}>REGION</Text>
          <Text style={styles.cardInfoValue}>
            {item.region?.toUpperCase() || '-'}
          </Text>
        </View>
      </View>
      {item.fbComments && (
        <Text style={styles.cardComments} numberOfLines={2}>
          "{item.fbComments}"
        </Text>
      )}
      <View style={styles.cardBottomRow}>
        <StageBadge
          stage={item.stage}
          size="small"
          showInternalDetail={showInternalDetail}
        />
        <Text style={styles.cardModified}>
          {formatDate(item.sysModifiedOn)}
        </Text>
      </View>
    </TouchableOpacity>
  );

  // Render dropdown
  const renderDropdown = (
    visible: boolean,
    options: { value: string; label: string }[],
    selectedValue: string,
    onSelect: (value: string) => void,
    onClose: () => void,
    testIdPrefix?: string,
  ) => {
    if (!visible) return null;
    return (
      <View style={styles.dropdownMenu}>
        {options.map(option => (
          <TouchableOpacity
            key={option.value}
            testID={
              testIdPrefix
                ? `${testIdPrefix}-${option.value || 'all'}`
                : undefined
            }
            style={[
              styles.dropdownItem,
              selectedValue === option.value && styles.dropdownItemSelected,
            ]}
            onPress={() => {
              onSelect(option.value);
              onClose();
            }}
          >
            <Text
              style={[
                styles.dropdownItemText,
                selectedValue === option.value &&
                  styles.dropdownItemTextSelected,
              ]}
            >
              {option.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    return (
      <View style={styles.paginationContainer}>
        <TouchableOpacity
          style={[
            styles.pageButton,
            currentPage === 1 && styles.pageButtonDisabled,
          ]}
          onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
        >
          <ChevronLeft
            size={16}
            color={currentPage === 1 ? '#ccc' : '#ff6b35'}
          />
        </TouchableOpacity>
        <Text style={styles.pageInfo}>
          Page {pagination.page} of {pagination.totalPages} ({pagination.total}{' '}
          items)
        </Text>
        <TouchableOpacity
          style={[
            styles.pageButton,
            currentPage === pagination.totalPages && styles.pageButtonDisabled,
          ]}
          onPress={() =>
            setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))
          }
          disabled={currentPage === pagination.totalPages}
        >
          <ChevronRight
            size={16}
            color={currentPage === pagination.totalPages ? '#ccc' : '#ff6b35'}
          />
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.container} testID="feedback-view-container">
      <Header
        currentPath={currentPath || '/feedback-view'}
        navigate={navigate}
      />
      <SubHeader title="Feedback View" onNavigate={navigate} />

      {Platform.OS === 'ios' && (
        <KeyboardDismissToolbar nativeID={ACCESSORY_VIEW_ID} />
      )}

      <View style={styles.content}>
        {/* Controls Row */}
        <View style={styles.controlsRow}>
          {/* Search */}
          <View style={styles.searchContainer}>
            <Search size={16} color="#666" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search by ID, customer, requestor, comments..."
              value={searchText}
              onChangeText={setSearchText}
              placeholderTextColor="#999"
              testID="feedback-search-input"
              returnKeyType="search"
              inputAccessoryViewID={
                Platform.OS === 'ios' ? ACCESSORY_VIEW_ID : undefined
              }
            />
          </View>

          {/* Filters */}
          <View style={styles.filtersRow}>
            {/* Rating Filter */}
            <View style={styles.filterContainer}>
              <TouchableOpacity
                style={styles.filterButton}
                testID="feedback-rating-filter"
                onPress={() => {
                  setShowRatingDropdown(!showRatingDropdown);
                  setShowModifiedDropdown(false);
                  setShowSortDropdown(false);
                }}
              >
                <Star size={14} color="#666" />
                <Text style={styles.filterButtonText}>
                  {RATING_OPTIONS.find(o => o.value === ratingFilter)?.label ||
                    'Rating'}
                </Text>
                <ChevronDown size={14} color="#666" />
              </TouchableOpacity>
              {renderDropdown(
                showRatingDropdown,
                RATING_OPTIONS,
                ratingFilter,
                setRatingFilter,
                () => setShowRatingDropdown(false),
                'feedback-rating-option',
              )}
            </View>

            {/* Modified Filter */}
            <View style={styles.filterContainer}>
              <TouchableOpacity
                style={styles.filterButton}
                testID="feedback-modified-filter"
                onPress={() => {
                  setShowModifiedDropdown(!showModifiedDropdown);
                  setShowRatingDropdown(false);
                  setShowSortDropdown(false);
                }}
              >
                <Text style={styles.filterButtonText}>
                  {MODIFIED_OPTIONS.find(o => o.value === modifiedFilter)
                    ?.label || 'Modified'}
                </Text>
                <ChevronDown size={14} color="#666" />
              </TouchableOpacity>
              {renderDropdown(
                showModifiedDropdown,
                MODIFIED_OPTIONS,
                modifiedFilter,
                setModifiedFilter,
                () => setShowModifiedDropdown(false),
                'feedback-modified-option',
              )}
            </View>

            {/* Sort */}
            <View style={styles.filterContainer}>
              <TouchableOpacity
                style={styles.filterButton}
                testID="feedback-sort-button"
                onPress={() => {
                  setShowSortDropdown(!showSortDropdown);
                  setShowRatingDropdown(false);
                  setShowModifiedDropdown(false);
                }}
              >
                <ArrowUpDown size={14} color="#666" />
                <Text style={styles.filterButtonText}>
                  {SORT_OPTIONS.find(o => o.value === sortField)?.label ||
                    'Sort'}
                </Text>
                <Text style={styles.sortDirection}>
                  {sortDirection === 'ASC' ? '\u2191' : '\u2193'}
                </Text>
              </TouchableOpacity>
              {showSortDropdown && (
                <View style={styles.dropdownMenu}>
                  {SORT_OPTIONS.map(option => (
                    <TouchableOpacity
                      key={option.value}
                      testID={`feedback-sort-option-${option.value}`}
                      style={[
                        styles.dropdownItem,
                        sortField === option.value &&
                          styles.dropdownItemSelected,
                      ]}
                      onPress={() => handleSortChange(option.value)}
                    >
                      <Text
                        style={[
                          styles.dropdownItemText,
                          sortField === option.value &&
                            styles.dropdownItemTextSelected,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <TouchableOpacity
                style={styles.clearFiltersButton}
                testID="feedback-clear-filters"
                onPress={handleClearFilters}
              >
                <Text style={styles.clearFiltersText}>Clear Filters</Text>
              </TouchableOpacity>
            )}

            {/* Refresh */}
            <TouchableOpacity
              style={styles.refreshButton}
              testID="feedback-refresh-button"
              onPress={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw
                size={16}
                color={isFetching ? '#ccc' : '#ff6b35'}
                style={isFetching ? styles.spinning : undefined}
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Content */}
        {isLoading ? (
          renderLoadingState()
        ) : error ? (
          renderErrorState()
        ) : feedbackItems.length === 0 ? (
          renderEmptyState()
        ) : shouldShowTable ? (
          <ScrollView
            style={styles.tableContainer}
            testID="feedback-table-container"
          >
            <View style={styles.table}>
              {renderTableHeader()}
              {feedbackItems.map((item, index) => renderTableRow(item, index))}
            </View>
          </ScrollView>
        ) : (
          <ScrollView style={styles.cardsContainer}>
            {feedbackItems.map(item => renderCard(item))}
          </ScrollView>
        )}

        {/* Pagination */}
        {renderPagination()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  content: {
    flex: 1,
    padding: 16,
    ...(isWeb && {
      maxWidth: 1400,
      alignSelf: 'center' as const,
      width: '100%',
    }),
  },
  controlsRow: {
    marginBottom: 16,
    zIndex: 100,
    position: 'relative',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 14,
    color: '#333',
    ...(isWeb && { outlineStyle: 'none' as any }),
  },
  filtersRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    zIndex: 10,
  },
  filterContainer: {
    position: 'relative',
    zIndex: 10,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    gap: 6,
  },
  filterButtonText: {
    fontSize: 13,
    color: '#333',
  },
  sortDirection: {
    fontSize: 14,
    color: '#ff6b35',
    fontWeight: '600',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: '#fff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginTop: 4,
    minWidth: 150,
    zIndex: 1000,
    ...(isWeb && {
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }),
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  dropdownItemSelected: {
    backgroundColor: '#e6f2ff',
  },
  dropdownItemText: {
    fontSize: 13,
    color: '#333',
  },
  dropdownItemTextSelected: {
    color: '#ff6b35',
    fontWeight: '600',
  },
  clearFiltersButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  clearFiltersText: {
    fontSize: 13,
    color: '#ff6b35',
    fontWeight: '500',
  },
  refreshButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginLeft: 'auto',
  },
  spinning: {
    opacity: 0.5,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 12,
  },
  errorMessage: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ff6b35',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    marginTop: 16,
    gap: 8,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 12,
  },
  emptyMessage: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
  },
  tableContainer: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    zIndex: 1,
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f3f5',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  headerCell: {
    fontSize: 13,
    fontWeight: '600',
    color: '#495057',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  evenRow: {
    backgroundColor: '#fafafa',
  },
  cell: {
    fontSize: 14,
    color: '#212529',
  },
  linkText: {
    color: '#ff6b35',
    fontWeight: '600',
  },
  requestIdColumn: {
    flex: 0.8,
    minWidth: 70,
  },
  nameColumn: {
    flex: 1.5,
    minWidth: 100,
  },
  regionColumn: {
    flex: 0.6,
    minWidth: 50,
  },
  stageColumn: {
    flex: 0.8,
    minWidth: 70,
  },
  stageBadgeWrapper: {
    alignSelf: 'flex-start',
  },
  ratingColumn: {
    flex: 0.9,
    minWidth: 80,
  },
  commentsColumn: {
    flex: 2.5,
    minWidth: 120,
  },
  dateColumn: {
    flex: 1,
    minWidth: 80,
  },
  starsContainer: {
    flexDirection: 'row',
    gap: 2,
  },
  noRating: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
  cardsContainer: {
    flex: 1,
    zIndex: 1,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  cardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardRequestId: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ff6b35',
  },
  cardCustomerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  cardInfoGrid: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 12,
  },
  cardInfoItem: {
    flex: 1,
  },
  cardInfoLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: '#999',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  cardInfoValue: {
    fontSize: 13,
    color: '#333',
  },
  cardComments: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
    marginBottom: 12,
    lineHeight: 18,
  },
  cardBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardModified: {
    fontSize: 12,
    color: '#999',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 16,
    gap: 16,
  },
  pageButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  pageButtonDisabled: {
    opacity: 0.5,
  },
  pageInfo: {
    fontSize: 13,
    color: '#666',
  },
});
